import { useId, type FC, useCallback, type CSSProperties, type ReactNode } from 'react';
import { useFragment, graphql } from 'react-relay';
import { useIntl } from 'dibs-react-intl';
import classnames from 'classnames';
import { handleLocaleUrl } from 'dibs-intl/exports/urls';
import { type Locale } from 'dibs-intl/exports/locales';

import { useTrackViewOnce, trackClick } from './tracking.ts';
import { HtmlBannerText } from './HtmlBannerText.tsx';
import HeroBannerLazyImage from './HeroBannerLazyImage.tsx';
import { useDeviceType } from '../../../nftHeader/hooks/useDeviceType.tsx';
import { type HeroBannerItem_heroBannerItemExhibition$key } from './__generated__/HeroBannerItem_heroBannerItemExhibition.graphql.ts';

import styles from './styles/HeroBannerItem.scss';

type Props = {
    index: number;
    inViewport?: boolean;
    isVisible?: boolean;
    heroBannerItemExhibition: HeroBannerItem_heroBannerItemExhibition$key | null | undefined;
    isFullWidth?: boolean;
    isTile?: boolean;
    tileCurator?: string;
    tileHref?: string;
    tileHeader?: string | null;
    tileEyeBrow?: string | null;
    tileDek?: string | null;
};

const MaybeLink: FC<{
    style?: CSSProperties;
    href?: string | null;
    className?: string;
    children?: ReactNode;
    locale: Locale;
}> = ({ href, children, locale, ...rest }) =>
    href ? (
        /** Cant use Link because this component needs access to the 'style' prop */
        // eslint-disable-next-line react/forbid-elements
        <a href={handleLocaleUrl(href, locale)} {...rest}>
            {children}
        </a>
    ) : (
        <div {...rest}>{children}</div>
    );
export const HeroBannerItem: FC<Props> = ({
    index,
    inViewport,
    isVisible,
    heroBannerItemExhibition: heroBannerItemExhibitionRef,
    isFullWidth: isFullWidthProp,
    isTile,
    tileCurator,
    tileHref,
    tileHeader,
    tileEyeBrow,
    tileDek,
}) => {
    const { isMobile } = useDeviceType();
    const htmlId = useId();
    const { locale } = useIntl();
    useTrackViewOnce(inViewport && isVisible, index + 1);
    const heroBannerExhibition = useFragment(
        graphql`
            fragment HeroBannerItem_heroBannerItemExhibition on NftCmsExhibitionHeroBannerType {
                format
                desktopImage {
                    url
                    height
                    width
                }
                mobileImage {
                    url
                    height
                    width
                }
                bannerConfig {
                    ... on NftCmsExhibitionStaticBannerType {
                        burnIn
                        ctaCopy
                        ctaHref
                    }
                    ... on NftCmsExhibitionHtmlBannerType {
                        imageSize
                        captionPosition
                        backgroundColor
                        ctaCopy
                        ctaHref
                        ...HtmlBannerText_htmlBannerExhibition
                    }
                }
            }
        `,
        heroBannerItemExhibitionRef
    );
    const { mobileImage, desktopImage, bannerConfig, format } = heroBannerExhibition || {};
    const image = isMobile && mobileImage ? mobileImage : desktopImage;
    const { imageSize, captionPosition, ctaHref, backgroundColor, burnIn, ctaCopy } =
        bannerConfig || {};
    const isStaticBanner = format !== 'html';
    const isFullWidth =
        typeof isFullWidthProp === 'boolean'
            ? isFullWidthProp
            : isStaticBanner || imageSize === 'full';
    const isReverse = captionPosition === 'right';
    const imageProps = {
        className: classnames(styles.imageContainer, {
            [styles.fullWidth]: isFullWidth,
        }),
        alt: (isStaticBanner && ctaCopy) || tileEyeBrow || undefined,
        src: image?.url || '',
        imageHeight: image?.height,
        imageWidth: image?.width,
    };
    const htmlBannerImageProps = {
        ...imageProps,
        href: tileHref ? null : ctaHref,
    };
    const staticBannerImageProps = {
        ...imageProps,
        href: tileHref ?? ctaHref,
    };

    const handleTrackingClick = useCallback(() => trackClick(index + 1), [index]);

    return format === 'html' ? (
        <MaybeLink
            href={tileHref}
            className={classnames(styles.htmlContainer, {
                [styles.reverse]: isReverse,
                [styles.visible]: isVisible,
                [styles.tile]: isTile,
                [styles.border]: !backgroundColor && !isFullWidth,
            })}
            style={
                (backgroundColor ? { '--background-color': backgroundColor } : {}) as CSSProperties
            }
            locale={locale}
        >
            <HtmlBannerText
                onClick={handleTrackingClick}
                htmlBannerExhibition={(heroBannerItemExhibitionRef && bannerConfig) || null}
                isTile={isTile}
                tileCurator={tileCurator}
                tileHeader={tileHeader}
                tileEyeBrow={tileEyeBrow}
                tileDek={tileDek}
            />
            <HeroBannerLazyImage
                onClick={handleTrackingClick}
                ariaHidden={!isStaticBanner && !isTile}
                {...htmlBannerImageProps}
            />
        </MaybeLink>
    ) : (
        <div className={styles.staticContainer}>
            <p id={htmlId} className={styles.visuallyHidden}>
                {burnIn}
            </p>
            <HeroBannerLazyImage
                onClick={handleTrackingClick}
                ariaDescribedBy={htmlId}
                {...staticBannerImageProps}
            />
        </div>
    );
};
